import { useState, useEffect } from 'react';
import { Typography, Switch, Modal, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, AppstoreOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { serviceTypeApi, businessApi } from '../../api/businessApi';
import { useBusinessStore } from '../../store/businessStore';
import { useLanguageStore } from '../../store/languageStore';
import { telegram } from '../../utils/telegram';
import { pickTranslatedName } from '../../utils/i18nDisplay';
import { ServiceTypeForm, buildServiceTypePayload } from './ServiceTypeForm';
import SettingsInfoButton from './SettingsInfoButton';
import { TranslationStatusBadge } from '../common/TranslationStatusBadge';
import ConfirmActionDialog from '../provider/bookings/ConfirmActionDialog';
import type { ServiceType } from '../../types/business';
import type { LanguageCode, TranslationStatusEntry } from '../../types/i18n';

const VALID_LANG_CODES: LanguageCode[] = ['uz', 'ru', 'en'];

export default function ServiceTypesSection() {
  const { t, i18n } = useTranslation(['common', 'owner']);
  const { selectedBusiness } = useBusinessStore();
  const currentLanguage = useLanguageStore((s) => s.currentLanguage);
  const tenantId = selectedBusiness?.id;
  if (!tenantId) return null;

  // Tenant-scoped languages — always fresh from selectedBusiness, not from
  // the global languageStore (which is only initialized once at bootstrap).
  const supportedLanguages = selectedBusiness.supportedLanguages.filter(
    (l): l is LanguageCode => VALID_LANG_CODES.includes(l as LanguageCode),
  );
  const defaultLanguage: LanguageCode = VALID_LANG_CODES.includes(
    selectedBusiness.defaultLanguage as LanguageCode,
  )
    ? (selectedBusiness.defaultLanguage as LanguageCode)
    : supportedLanguages[0] ?? 'uz';

  const [types, setTypes] = useState<ServiceType[]>([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [translations, setTranslations] = useState<Partial<Record<LanguageCode, string>>>({});
  const [translationStatuses, setTranslationStatuses] = useState<TranslationStatusEntry[]>([]);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ServiceType | null>(null);

  useEffect(() => { void loadTypes(); }, []);

  // Backend returns the `name` field translated via Accept-Language; refetch
  // when the user switches interface language so the list updates.
  useEffect(() => {
    const handler = () => { void loadTypes(); };
    i18n.on('languageChanged', handler);
    return () => { i18n.off('languageChanged', handler); };
  }, [i18n]);

  const loadTypes = async () => {
    setLoading(true);
    try {
      const data = await serviceTypeApi.getAll(tenantId);
      setTypes(data);
      // Best-effort: translation status
      if (supportedLanguages.length > 1) {
        try {
          const statuses = await businessApi.getTranslationStatus(tenantId);
          setTranslationStatuses(statuses);
        } catch {
          // ignore — badges just won't show
        }
      }
    } catch {
      void message.error(t('owner:settings.serviceTypes.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setNewName('');
    setTranslations({});
    setAddOpen(true);
    telegram.hapticFeedback.impact('light');
  };

  const handleCancelAdd = () => {
    if (adding) return;
    setAddOpen(false);
    setNewName('');
    setTranslations({});
  };

  const handleAdd = async () => {
    const payload = buildServiceTypePayload(supportedLanguages, newName, translations, defaultLanguage);
    if (!payload) {
      const msg = supportedLanguages.length > 1
        ? t('owner:settings.serviceTypes.errorMinLengthByLang', { lang: defaultLanguage.toUpperCase() })
        : t('owner:settings.serviceTypes.errorMinLength');
      void message.warning(msg);
      telegram.hapticFeedback.notification('error');
      return;
    }
    setAdding(true);
    try {
      const created = await serviceTypeApi.create(tenantId, payload);
      setTypes((prev) => [...prev, created]);
      setNewName('');
      setTranslations({});
      setAddOpen(false);
      telegram.hapticFeedback.notification('success');
    } catch (err: unknown) {
      const error = err as { response?: { status?: number; data?: { message?: string } } };
      if (error?.response?.status === 409) {
        void message.error(t('owner:settings.serviceTypes.errorDuplicate'));
      } else {
        void message.error(error?.response?.data?.message ?? t('owner:settings.serviceTypes.errorAdding'));
      }
      telegram.hapticFeedback.notification('error');
    } finally {
      setAdding(false);
    }
  };

  const handleToggle = async (item: ServiceType) => {
    try {
      const updated = item.active
        ? await serviceTypeApi.deactivate(tenantId, item.id)
        : await serviceTypeApi.activate(tenantId, item.id);
      setTypes((prev) => prev.map((tp) => (tp.id === item.id ? updated : tp)));
      telegram.hapticFeedback.impact('light');
    } catch {
      void message.error(t('owner:settings.serviceTypes.errorToggling'));
    }
  };

  const handleDelete = (item: ServiceType) => {
    if (deletingId !== null) return;
    telegram.hapticFeedback.impact('light');
    setDeleteTarget(item);
  };

  const handleCancelDelete = () => {
    if (deletingId !== null) return;
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    const item = deleteTarget;
    if (!item) return;
    setDeletingId(item.id);
    try {
      await serviceTypeApi.remove(tenantId, item.id);
      setTypes((prev) => prev.filter((tp) => tp.id !== item.id));
      telegram.hapticFeedback.notification('success');
      void message.success(t('owner:settings.serviceTypes.deletedMessage'));
      setDeleteTarget(null);
    } catch (err: unknown) {
      const error = err as {
        response?: {
          status?: number;
          data?: {
            code?: string;
            message?: string;
            data?: { usedByProviders?: number };
          };
        };
      };
      const code = error?.response?.data?.code;
      const httpStatus = error?.response?.status;
      telegram.hapticFeedback.notification('error');
      if (code === 'SERVICE_TYPE_IN_USE' || httpStatus === 409) {
        const count = error?.response?.data?.data?.usedByProviders;
        const msg = typeof count === 'number' && count > 0
          ? t('owner:settings.serviceTypes.errorInUseN', { count })
          : t('owner:settings.serviceTypes.errorInUse');
        void message.error(msg);
        setDeleteTarget(null);
      } else if (httpStatus === 404) {
        void message.error(t('owner:settings.serviceTypes.errorNotFound'));
        setTypes((prev) => prev.filter((tp) => tp.id !== item.id));
        setDeleteTarget(null);
      } else {
        void message.error(error?.response?.data?.message ?? t('owner:settings.serviceTypes.errorDeleting'));
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handleEditSave = async () => {
    if (!editingId) return;
    const payload = buildServiceTypePayload(supportedLanguages, editName, translations, defaultLanguage);
    if (!payload) {
      const msg = supportedLanguages.length > 1
        ? t('owner:settings.serviceTypes.errorMinLengthByLang', { lang: defaultLanguage.toUpperCase() })
        : t('owner:settings.serviceTypes.errorMinLength');
      void message.warning(msg);
      telegram.hapticFeedback.notification('error');
      return;
    }
    setEditSaving(true);
    try {
      const updated = await serviceTypeApi.update(tenantId, editingId, payload);
      setTypes((prev) => prev.map((tp) => (tp.id === editingId ? updated : tp)));
      setEditingId(null);
      setTranslations({});
      telegram.hapticFeedback.notification('success');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      void message.error(error?.response?.data?.message ?? t('owner:settings.serviceTypes.errorEditing'));
      telegram.hapticFeedback.notification('error');
    } finally {
      setEditSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (editSaving) return;
    setEditingId(null);
    setTranslations({});
  };

  const getStatusEntry = (itemId: number) =>
    translationStatuses.find((s) => s.entityId === itemId && s.entityType === 'SERVICE_TYPE');

  const activeCount = types.filter((tp) => tp.active).length;

  return (
    <>
      <div style={{ borderRadius: 16, background: 'var(--bl-surface, #fff)', border: '1px solid var(--bl-divider, #ebebeb)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '12px 14px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--bl-divider, #ebebeb)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--bl-success-soft, #e8f7ee)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: 'var(--bl-success, #1f8b4c)' }}>
              <AppstoreOutlined />
            </div>
            <Typography.Text style={{ fontSize: 15, fontWeight: 700, color: 'var(--bl-text-strong, #0f172a)' }}>
              {t('owner:settings.serviceTypes.title')}
            </Typography.Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {types.length > 0 && (
              <div style={{ background: 'var(--bl-success-soft, #e8f7ee)', borderRadius: 20, padding: '3px 10px' }}>
                <Typography.Text style={{ fontSize: 12, fontWeight: 700, color: 'var(--bl-success, #1f8b4c)' }}>
                  {activeCount} / {types.length}
                </Typography.Text>
              </div>
            )}
            <SettingsInfoButton
              title={t('owner:settings.serviceTypes.info.title')}
              body={t('owner:settings.serviceTypes.info.body')}
            />
          </div>
        </div>

        <div style={{ padding: '12px 14px 14px' }}>
          {/* Add button */}
          <button
            type="button"
            onClick={handleOpenAdd}
            style={{ width: '100%', height: 44, borderRadius: 12, background: 'var(--bl-success, #1f8b4c)', border: 'none', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: types.length > 0 ? 12 : 0, WebkitTapHighlightColor: 'transparent' }}
          >
            <PlusOutlined /> {t('owner:settings.serviceTypes.addButton')}
          </button>

          {/* List */}
          {loading ? (
            <Typography.Text style={{ fontSize: 13, color: 'var(--bl-text-muted, #9ca3af)' }}>{t('common:status.loading')}</Typography.Text>
          ) : types.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <Typography.Text style={{ fontSize: 13, color: 'var(--bl-text-muted, #9ca3af)' }}>
                {t('owner:settings.serviceTypes.empty')}
              </Typography.Text>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {types.map((item, idx) => (
                <div key={item.id}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 2px' }}>
                    {/* Name + badge */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Typography.Text style={{ fontSize: 14, fontWeight: 600, color: item.active ? 'var(--bl-text-strong, #0f172a)' : 'var(--bl-text-muted, #9ca3af)' }}>
                        {pickTranslatedName(item, currentLanguage, defaultLanguage)}
                      </Typography.Text>
                      <TranslationStatusBadge
                        entry={getStatusEntry(item.id)}
                        supportedLanguages={supportedLanguages}
                      />
                    </div>
                    {/* Edit */}
                    <button
                      type="button"
                      onClick={() => { setEditingId(item.id); setEditName(item.name); setTranslations({}); }}
                      style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--bl-surface-soft, #f0f4f8)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: 'var(--bl-text-muted, #64748b)', flexShrink: 0 }}
                      aria-label={t('owner:settings.serviceTypes.edit')}
                    >
                      <EditOutlined />
                    </button>
                    {/* Delete */}
                    <button
                      type="button"
                      onClick={() => handleDelete(item)}
                      disabled={deletingId === item.id}
                      style={{
                        width: 30, height: 30, borderRadius: 8,
                        background: '#fee2e2',
                        border: 'none',
                        cursor: deletingId === item.id ? 'wait' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, color: '#dc2626', flexShrink: 0,
                        opacity: deletingId === item.id ? 0.6 : 1,
                      }}
                      aria-label={t('owner:settings.serviceTypes.deleteAriaLabel')}
                    >
                      <DeleteOutlined />
                    </button>
                    {/* Toggle */}
                    <Switch size="small" checked={item.active} onChange={() => void handleToggle(item)} />
                  </div>
                  {idx < types.length - 1 && (
                    <div style={{ height: 1, background: 'var(--bl-divider, #ebebeb)' }} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add modal */}
      <Modal
        title={t('owner:settings.serviceTypes.addModalTitle')}
        open={addOpen}
        onOk={() => void handleAdd()}
        onCancel={handleCancelAdd}
        okText={t('common:actions.add')}
        cancelText={t('common:actions.cancel')}
        confirmLoading={adding}
        maskClosable={!adding}
        destroyOnClose
      >
        <ServiceTypeForm
          singleName={newName}
          onSingleNameChange={setNewName}
          translations={translations}
          onTranslationsChange={setTranslations}
          supportedLanguages={supportedLanguages}
          defaultLanguage={defaultLanguage}
        />
      </Modal>

      {/* Delete confirmation — bottom sheet */}
      <ConfirmActionDialog
        open={deleteTarget !== null}
        title={t('owner:settings.serviceTypes.deleteModalTitle')}
        description={
          deleteTarget
            ? t('owner:settings.serviceTypes.deleteConfirmText', {
                name: pickTranslatedName(deleteTarget, currentLanguage, defaultLanguage),
              })
            : undefined
        }
        confirmText={t('common:actions.delete')}
        cancelText={t('common:actions.cancel')}
        danger
        loading={deletingId !== null}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={handleCancelDelete}
      />

      {/* Edit modal */}
      <Modal
        title={t('owner:settings.serviceTypes.editModalTitle')}
        open={editingId !== null}
        onOk={() => void handleEditSave()}
        onCancel={handleCancelEdit}
        okText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
        confirmLoading={editSaving}
        maskClosable={!editSaving}
        destroyOnClose
      >
        <ServiceTypeForm
          initialData={types.find((tp) => tp.id === editingId) ?? null}
          singleName={editName}
          onSingleNameChange={setEditName}
          translations={translations}
          onTranslationsChange={setTranslations}
          supportedLanguages={supportedLanguages}
          defaultLanguage={defaultLanguage}
        />
      </Modal>
    </>
  );
}
